/**
 * Client half of the TA board's server-side Gmail connection (proxy routes/ta-gmail-oauth.js).
 *
 * The proxy holds a refresh token per recruiter and mints one-hour access tokens from it on request,
 * so the browser never has to open a Google window after the first connect — the pattern the MyNoats
 * page in FINN Spaces uses. `useGmailToken.ts` is the only consumer.
 *
 * Every outcome the hook has to branch on is a value, not an exception: whether the deployment has
 * the flow configured at all decides whether the popup fallback is used, and that must not surface
 * as a global error.
 */
import { ApiRequestError, authenticatedFetch } from '../../../shared/utils/authenticatedFetch';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

const BASE = `${PROXY_URL}/api/ta-action-board/gmail`;

export interface ServerGrantToken {
  accessToken: string;
  /**
   * Lifetime in SECONDS, as Google reported it. The absolute expiry is computed on the client from
   * its own clock (gmailServerGrant.ts) — an epoch stamped by the proxy would be compared against a
   * browser clock that may be minutes off, and a late renewal is exactly the banner this removes.
   */
  expiresIn: number;
  /** What Google GRANTED, from the refresh response. */
  scopes: string[];
}

export type MintResult =
  | { kind: 'token'; token: ServerGrantToken }
  /** This deployment has no OAuth client configured — use the popup path. */
  | { kind: 'unavailable' }
  /** No grant for this recruiter yet — show Connect. */
  | { kind: 'not_connected' }
  /** The grant is dead or too narrow and was dropped — Connect runs the consent flow. */
  | { kind: 'needs_reconnect'; reason: string }
  /** Transient failure (network, Google 5xx) — keep whatever token is cached, retry later. */
  | { kind: 'error'; message: string };

/**
 * Ask the proxy for a fresh access token from the stored grant. Silent: no window, no click.
 *
 * All expected statuses are mapped to values; 503 is passed to `suppressErrorStatuses` so an
 * unconfigured deployment does not show up as an API error every time the board mounts.
 */
export async function mintGmailToken(): Promise<MintResult> {
  try {
    // Suppressed statuses come back as a Response instead of throwing (authenticatedFetch), so they
    // are read off `res.status` here rather than from an exception.
    const res = await authenticatedFetch(`${BASE}/token`, {
      method: 'POST',
      suppressErrorStatuses: [404, 409, 503],
    });
    // The proxy's own answers carry a JSON body; a bare status is something else standing in front
    // of it (a Cloud Run 503 during a cold start, an older proxy build without these routes answering
    // Express's default 404). Only the proxy's explicit "unconfigured" may switch the page to the
    // popup path for good; anything else is either "use the fallback for now" or a transient error.
    if (res.status === 503) return (await proxyError(res)) === 'unconfigured' ? { kind: 'unavailable' } : { kind: 'error', message: 'The proxy is temporarily unavailable' };
    if (res.status === 404) {
      const body = await jsonOrNull<{ connected?: boolean }>(res);
      return body?.connected === false ? { kind: 'not_connected' } : { kind: 'unavailable' };
    }
    if (res.status === 409) {
      const body = await jsonOrNull<{ reason?: string }>(res);
      return { kind: 'needs_reconnect', reason: body?.reason || 'needs_reconnect' };
    }
    const body = await jsonOrNull<Partial<ServerGrantToken>>(res);
    if (typeof body?.accessToken !== 'string' || typeof body.expiresIn !== 'number') {
      return { kind: 'error', message: 'The proxy returned no token' };
    }
    return {
      kind: 'token',
      token: {
        accessToken: body.accessToken,
        expiresIn: body.expiresIn,
        scopes: Array.isArray(body.scopes) ? body.scopes : [],
      },
    };
  } catch (err) {
    const message =
      err instanceof ApiRequestError ? err.message : (err as Error)?.message || 'Could not reach the proxy';
    return { kind: 'error', message };
  }
}

async function jsonOrNull<T>(res: Response): Promise<T | null> {
  try {
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

/** The proxy's `error` field, or '' when the body is not the proxy's JSON. */
async function proxyError(res: Response): Promise<string> {
  const body = await jsonOrNull<{ error?: string }>(res);
  return typeof body?.error === 'string' ? body.error : '';
}

export type ConnectStart =
  | { kind: 'redirect'; authorizeUrl: string }
  | { kind: 'unavailable' }
  | { kind: 'error'; message: string };

/**
 * Start the consent flow. The proxy stores a single-use state bound to the signed-in recruiter and
 * returns Google's authorize URL; the caller navigates there and Google sends the browser back to
 * `returnTo` (same-origin path) with `?gmail=connected` or `?gmail_error=<code>`.
 */
export async function startGmailConnect(returnTo: string): Promise<ConnectStart> {
  try {
    const res = await authenticatedFetch(`${BASE}/connect`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ returnTo }),
      suppressErrorStatuses: [404, 503],
    });
    // An older proxy build (no such route → Express 404) or an unconfigured one: use the popup path.
    if (res.status === 404 || res.status === 503) return { kind: 'unavailable' };
    const body = await jsonOrNull<{ authorizeUrl?: string }>(res);
    // Only ever navigate to Google: a proxy answer pointing anywhere else is refused.
    if (typeof body?.authorizeUrl !== 'string' || !/^https:\/\/accounts\.google\.com\//.test(body.authorizeUrl)) {
      return { kind: 'error', message: 'The proxy returned no Google URL' };
    }
    return { kind: 'redirect', authorizeUrl: body.authorizeUrl };
  } catch (err) {
    return { kind: 'error', message: (err as Error)?.message || 'Could not start the Google connection' };
  }
}

/** Revoke the grant at Google and forget it. Best-effort from the caller's point of view. */
export async function disconnectGmail(): Promise<void> {
  await authenticatedFetch(`${BASE}/disconnect`, { method: 'POST' });
}
